import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Heading, List, Link2, Code2 } from "lucide-react";
import { CALLOUT_KINDS, calloutHtml } from "@/lib/callouts";

/**
 * Rich HTML editor that preserves inline styles + table markup for email-safe HTML.
 * Implemented on plain contenteditable to guarantee round-trip of arbitrary
 * inline-styled tables (per user requirement: email-safe HTML fidelity matters
 * more than editor polish).
 */
export default function RichEditor({ value, onChange, mode, setMode }) {
    const ref = useRef(null);
    const [calloutOpen, setCalloutOpen] = useState(false);
    const lastSavedRange = useRef(null);

    // Keep DOM in sync with value only when the editor doesn't currently hold
    // the same content (avoid caret jumps).
    useEffect(() => {
        if (mode !== "rich") return;
        if (!ref.current) return;
        if (ref.current.innerHTML !== (value || "")) {
            ref.current.innerHTML = value || "";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, value]);

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
            const r = sel.getRangeAt(0);
            if (ref.current && ref.current.contains(r.commonAncestorContainer)) {
                lastSavedRange.current = r.cloneRange();
            }
        }
    };

    const restoreSelection = () => {
        if (!lastSavedRange.current || !ref.current) return false;
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(lastSavedRange.current);
        return true;
    };

    const emit = () => {
        if (!ref.current) return;
        onChange(ref.current.innerHTML);
    };

    const exec = (cmd, arg) => {
        ref.current?.focus();
        restoreSelection();
        document.execCommand(cmd, false, arg);
        emit();
    };

    const insertHtml = (html) => {
        ref.current?.focus();
        restoreSelection();
        document.execCommand("insertHTML", false, html);
        emit();
    };

    const insertLink = () => {
        const url = window.prompt("Enter URL");
        if (url) exec("createLink", url);
    };

    const insertCallout = (kind) => {
        insertHtml(calloutHtml(kind) + "<p></p>");
        setCalloutOpen(false);
    };

    return (
        <div>
            {/* Toolbar */}
            <div
                className="flex flex-wrap items-center gap-1 p-2 rounded-t-[10px] border"
                style={{ background: "#f7f8fa", borderColor: "var(--tms-border)" }}
            >
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={() => exec("bold")} title="Bold" data-testid="editor-bold-btn">
                    <Bold size={13} />
                </button>
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={() => exec("italic")} title="Italic" data-testid="editor-italic-btn">
                    <Italic size={13} />
                </button>
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={() => exec("underline")} title="Underline" data-testid="editor-underline-btn">
                    <Underline size={13} />
                </button>
                <div className="mx-1" style={{ width: 1, height: 20, background: "var(--tms-border)" }} />
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={() => exec("formatBlock", "H2")} title="Heading" data-testid="editor-heading-btn">
                    <Heading size={13} />
                </button>
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={() => exec("insertUnorderedList")} title="Bullet list" data-testid="editor-list-btn">
                    <List size={13} />
                </button>
                <button type="button" className="edit-toolbar-btn" onMouseDown={(e)=>e.preventDefault()} onClick={insertLink} title="Link" data-testid="editor-link-btn">
                    <Link2 size={13} />
                </button>

                <div className="mx-1" style={{ width: 1, height: 20, background: "var(--tms-border)" }} />

                {/* Callout dropdown */}
                <div className="relative">
                    <button
                        type="button"
                        className="edit-toolbar-btn"
                        onMouseDown={(e)=>e.preventDefault()}
                        onClick={() => { saveSelection(); setCalloutOpen((o) => !o); }}
                        title="Insert callout"
                        data-testid="editor-callout-btn"
                    >
                        Callout ▾
                    </button>
                    {calloutOpen && (
                        <div
                            className="absolute z-30 mt-1 tms-card p-1 w-56"
                            style={{ boxShadow: "0 8px 24px rgba(26,62,109,0.14)" }}
                            data-testid="editor-callout-menu"
                        >
                            {CALLOUT_KINDS.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    className="w-full text-left px-3 py-2 rounded-md hover:bg-[#eef0f3]"
                                    style={{ fontSize: 13, color: "var(--tms-navy)" }}
                                    onMouseDown={(e)=>e.preventDefault()}
                                    onClick={() => insertCallout(c.key)}
                                    data-testid={`callout-option-${c.key}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="ml-auto">
                    <button
                        type="button"
                        className={`edit-toolbar-btn ${mode === "html" ? "is-active" : ""}`}
                        onClick={() => setMode(mode === "html" ? "rich" : "html")}
                        title="Toggle HTML mode"
                        data-testid="editor-html-mode-btn"
                    >
                        <Code2 size={13} /> HTML
                    </button>
                </div>
            </div>

            {mode === "rich" ? (
                <div
                    ref={ref}
                    className="rich-body"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onBlur={() => { saveSelection(); emit(); }}
                    onMouseUp={saveSelection}
                    onKeyUp={saveSelection}
                    data-testid="editor-body-contenteditable"
                />
            ) : (
                <textarea
                    className="rich-body"
                    style={{ fontFamily: "JetBrains Mono, ui-monospace, Menlo, monospace", fontSize: 12 }}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid="editor-body-html-textarea"
                />
            )}
        </div>
    );
}
